import React, { useCallback, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { connect } from "react-redux";
import { useIntl } from "react-intl";

import DisplaysActioner from "services/displays/actioner";
import DisplaysUtils from "services/displays/utils";
import useLiveReload from "services/hooks/useLiveReload";

import ParkkiTheme from "themes/ParkkiTheme";

import LiveButton from "components/LiveButton";
import Section from "components/Section";
import SelectALayerInfo from "components/SelectALayerInfo";
import StatsRow from "components/StatsRow";
import DisplayFormContainer from "../DisplayFormContainer/DisplayFormContainer";

const Container = styled.div`
  display: flex;
  flex: 1;
  background-color: ${ParkkiTheme.colors.lightGrey};
  flex-direction: column;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
  margin-top: 8px;
  gap: ${ParkkiTheme.spacings.small};
`;

const TopContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  margin-top: ${ParkkiTheme.spacings.xLarge};
`;

function DisplaysLayout({
  currentFloor,
  onTimestampChange,
  selectedLayer,
  setAutoRefresh,
  setSelectedLayers,
  displays,
}) {
  const { formatMessage } = useIntl();

  useEffect(() => {
    let newSelectedLayer;

    if (selectedLayer?.floor_level === currentFloor) {
      newSelectedLayer = selectedLayer;
    }

    setSelectedLayers(newSelectedLayer ? [newSelectedLayer] : []);
  }, [currentFloor]);

  const handleDateChange = useCallback(
    (updatedDateTime) => {
      const timestamp = Math.round(updatedDateTime?.valueOf() / 1000);
      onTimestampChange(timestamp);
    },
    [onTimestampChange]
  );

  const handleReloadOnce = useCallback(() => {
    handleDateChange(new Date().getTime());
  }, [handleDateChange]);

  const handleSetAutoRefresh = useCallback(() => {
    setAutoRefresh(true);
  }, [setAutoRefresh]);

  const currentDisplay = useMemo(
    () => displays?.find((d) => d?.id === selectedLayer?.id),
    [displays, selectedLayer]
  );

  const displayData = useMemo(() => {
    return DisplaysUtils.getDisplayData(currentDisplay);
  }, [currentDisplay]);

  const { liveReloadActive, onActivateLiveReload, onCancelLiveReload } =
    useLiveReload({
      key: "PARK_STATS_PAGE_LIVE_RELOAD_INTERVAL_ID",
      onSetAutoRefresh: handleSetAutoRefresh,
      onReload: handleReloadOnce,
      force: true,
    });

  function handleAutoReloadChecked(checked) {
    const callback = checked ? onActivateLiveReload : onCancelLiveReload;
    return callback();
  }

  return (
    <Container>
      <FiltersContainer>
        <LiveButton
          withLiveReload={liveReloadActive}
          onReload={handleReloadOnce}
          onChecked={handleAutoReloadChecked}
        />
      </FiltersContainer>

      {!selectedLayer && (
        <TopContainer>
          <SelectALayerInfo
            message={formatMessage({ id: "displaysScene.noSelectedLayerInfo" })}
          />
        </TopContainer>
      )}

      {!!selectedLayer && (
        <Section
          title={formatMessage({ id: "displaysScene.currentDisplayData" })}
        >
          <StatsRow
            initialized={!!selectedLayer}
            stats={displayData}
            numberOfSection={4}
          />
        </Section>
      )}
      {!!selectedLayer && (
        <Section
          title={formatMessage({ id: "displaysScene.currentDisplayEdit" })}
          isAwol={currentDisplay.is_awol}
          awolMessage={formatMessage({
            id: "displaysScene.awolMessage",
          })}
        >
          <DisplayFormContainer />
        </Section>
      )}
    </Container>
  );
}

const mapStateToProps = (state) => ({
  currentFloor: state.Map?.config?.currentFloor,
  selectedLayer: DisplaysUtils.getSelectedLayer(state.Displays),
  displays: state.Displays.list.items,
});

const mapDispatchToProps = (dispatch) => ({
  setAutoRefresh: (autoRefresh) =>
    dispatch(DisplaysActioner.setAutoRefresh(autoRefresh)),
  setSelectedLayers: (layers) =>
    dispatch(DisplaysActioner.setSelectedLayers(layers)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DisplaysLayout);
